<template>
	<div>
		<div class="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
			<WSubTitle text="Settlement summary" />
			<span v-if="tcm?.available" class="text-sm text-color-secondary">
				{{ tcm.valid_hour_count }} valid hour(s) · VAT {{ fmt(tcm.vat) }} SAR
			</span>
		</div>

		<div v-if="!tcm?.available" class="dash-empty">
			<i class="mdi mdi-file-document-outline dash-empty-icon" />
			<p>No TCM results for this period yet.</p>
			<PButton
				v-if="canRunTcm"
				:disabled="!billingPeriodId || runningTcm"
				:loading="runningTcm"
				class="mt-2"
				icon="pi pi-play"
				label="Run TCM"
				rounded
				size="small"
				@click="$emit('run-tcm')"
			/>
		</div>

		<template v-else>
			<PDataTable
				:rowHover="true"
				:showGridlines="true"
				:stripedRows="true"
				:value="tcm.variables"
				data-key="name"
				size="small"
			>
				<PColumn field="name" header="Tag" sortable />
				<PColumn field="value" header="Value" sortable>
					<template #body="{ data }">
						{{ fmt(data.value) }}
					</template>
				</PColumn>
				<PColumn field="unit" header="Unit" style="width: 5rem;" />
				<PColumn field="description" header="Description" />
			</PDataTable>

			<div v-if="invoices.length" class="mt-3">
				<div class="section-label mb-2">Invoices for this period</div>
				<PDataTable
					:rowHover="true"
					:showGridlines="true"
					:value="invoices"
					data-key="id"
					size="small"
				>
					<PColumn field="invoice_number" header="Number" />
					<PColumn field="status" header="Status" style="width: 7rem;">
						<template #body="{ data }">
							<PTag :severity="data.status === 'approved' ? 'success' : 'warning'">
								{{ data.status }}
							</PTag>
						</template>
					</PColumn>
					<PColumn field="mode" header="Mode" style="width: 6rem;" />
					<PColumn field="pdf_filename" header="PDF">
						<template #body="{ data }">
							<a
								v-if="data.pdf_filename"
								class="cursor-pointer"
								@click.prevent="downloadInvoice(data.pdf_filename)"
							>{{ data.pdf_filename }}</a>
							<span v-else>—</span>
						</template>
					</PColumn>
					<PColumn field="generated_by" header="Generated by" />
					<PColumn field="approved_by" header="Approved by" />
					<PColumn v-if="canApproveInvoice" header="" style="width: 7rem;">
						<template #body="{ data }">
							<PButton
								v-if="data.status === 'draft'"
								:loading="approvingId === data.id"
								icon="pi pi-check"
								label="Approve"
								rounded
								size="small"
								severity="success"
								@click="$emit('approve-invoice', data.id)"
							/>
						</template>
					</PColumn>
				</PDataTable>
			</div>
		</template>
	</div>
</template>

<script lang="ts">
	import { defineComponent, PropType } from 'vue'
	import Constants from '@/helpers/Constants'
	import { DashboardInvoice, DashboardTcm } from '@/brokers/DashboardBroker'

	export default defineComponent({
		name: 'DashSettlement',
		props: {
			tcm: { type: Object as PropType<DashboardTcm | null>, default: null },
			invoices: {
				type: Array as PropType<DashboardInvoice[]>,
				default: () => [],
			},
			billingPeriodId: { type: Number as PropType<number | null>, default: null },
			canRunTcm: { type: Boolean, default: false },
			canApproveInvoice: { type: Boolean, default: false },
			runningTcm: { type: Boolean, default: false },
			approvingId: { type: Number as PropType<number | null>, default: null },
		},
		emits: ['run-tcm', 'approve-invoice'],
		methods: {
			fmt(v: number | null | undefined): string {
				if (v == null || Number.isNaN(v)) return '—'
				return Number(v).toLocaleString(undefined, { maximumFractionDigits: 5 })
			},
			downloadInvoice(filename: string) {
				window.open(
					`${Constants.SERVER_URL}/download_invoice?comp=invoice&filename=${encodeURIComponent(filename)}`,
					'_blank',
				)
			},
		},
	})
</script>

<style scoped>
	.dash-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 2rem 1rem;
		color: var(--text-color-secondary);
		gap: 0.5rem;
		border: 1px dashed var(--surface-border);
		border-radius: 6px;
	}
	.dash-empty-icon {
		font-size: 2rem;
		opacity: 0.5;
	}
	.section-label {
		font-size: 0.85rem;
		font-weight: 600;
	}
</style>
